/**
 * up-collection: filter + sort behaviour for sections/up-collection.liquid.
 *
 * Everything in this file is a progressive enhancement. Every filter input,
 * every sort control and every fallback tag link is a plain, real GET
 * form/link pointing at a working Shopify URL (native collection.filters
 * params, or /collections/handle/tag routing). With this script disabled
 * the page still filters and sorts correctly via full page reloads.
 *
 * With JS on, form submissions and tag-link clicks are intercepted and
 * replayed against Shopify's Section Rendering API
 * (`?section_id=<id>`), and the response is used to swap just the
 * sidebar / results / mobile-drawer content back into the page, so
 * filtering and sorting don't reload the whole document.
 */
(function () {
  'use strict';

  var SWAP_SELECTORS = [
    '[data-up-collection-sidebar]',
    '.up-collection__main',
    '.up-collection__drawer-body',
    '.up-collection__drawer-footer'
  ];

  var applyStateByForm = new WeakMap();

  function initSection(section) {
    var sectionId = section.getAttribute('data-section-id');
    var drawer = section.querySelector('[data-up-collection-drawer]');
    var floatBtn = section.querySelector('[data-up-collection-float-btn]');

    function onKeydown(event) {
      if (event.key === 'Escape') closeDrawer();
    }

    function openDrawer() {
      if (!drawer) return;
      drawer.hidden = false;
      requestAnimationFrame(function () {
        drawer.classList.add('is-open');
      });
      if (floatBtn) floatBtn.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';
      document.addEventListener('keydown', onKeydown);
      snapshotApplyState();
    }

    function closeDrawer() {
      if (!drawer || drawer.hidden) return;
      drawer.classList.remove('is-open');
      if (floatBtn) floatBtn.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
      document.removeEventListener('keydown', onKeydown);
      window.setTimeout(function () {
        drawer.hidden = true;
      }, 340);
    }

    function snapshotApplyState() {
      var mobileForm = section.querySelector('#up-collection-filter-form-mobile');
      if (!mobileForm) return;
      applyStateByForm.set(mobileForm, serialize(mobileForm));
      updateApplyLabel(mobileForm);
    }

    function updateApplyLabel(form) {
      var applyBtn = section.querySelector('[data-up-collection-apply]');
      if (!applyBtn || !form) return;
      var initial = applyStateByForm.get(form) || '';
      var current = serialize(form);
      if (current === initial) {
        applyBtn.textContent = 'Apply';
        return;
      }
      var before = initial ? initial.split('&') : [];
      var after = current ? current.split('&') : [];
      var beforeSet = {};
      var afterSet = {};
      before.forEach(function (pair) { beforeSet[pair] = true; });
      after.forEach(function (pair) { afterSet[pair] = true; });
      var changed = 0;
      before.forEach(function (pair) { if (!afterSet[pair]) changed++; });
      after.forEach(function (pair) { if (!beforeSet[pair]) changed++; });
      applyBtn.textContent = changed > 0 ? 'Apply (' + changed + ')' : 'Apply';
    }

    function serialize(form) {
      return new URLSearchParams(new FormData(form)).toString();
    }

    function buildUrlFromForm(form) {
      var action = form.getAttribute('action') || window.location.pathname;
      var query = serialize(form);
      return query ? action + '?' + query : action;
    }

    function fetchSwap(url) {
      var target = new URL(url, window.location.origin);
      var fetchUrl = new URL(target.toString());
      fetchUrl.searchParams.set('section_id', sectionId);

      return fetch(fetchUrl.toString(), {
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      })
        .then(function (res) {
          if (!res.ok) throw new Error('up-collection: bad response');
          return res.text();
        })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          SWAP_SELECTORS.forEach(function (selector) {
            var oldEl = section.querySelector(selector);
            var newEl = doc.querySelector(selector);
            if (oldEl && newEl) oldEl.replaceWith(newEl);
          });
          window.history.pushState({ upCollection: true }, '', target.pathname + target.search);
          return true;
        })
        .catch(function () {
          window.location.href = url;
          return false;
        });
    }

    function submitFormViaFetch(form) {
      return fetchSwap(buildUrlFromForm(form));
    }

    section.addEventListener('change', function (event) {
      var desktopFilterForm = event.target.closest('#up-collection-filter-form-desktop');
      if (desktopFilterForm) {
        desktopFilterForm.requestSubmit();
        return;
      }

      var sortSelect = event.target.closest('[data-up-collection-sort-select]');
      if (sortSelect) {
        var sortForm = sortSelect.closest('[data-up-collection-sort-form]');
        if (sortForm) sortForm.requestSubmit();
        return;
      }

      var mobileFilterForm = event.target.closest('#up-collection-filter-form-mobile');
      if (mobileFilterForm) {
        updateApplyLabel(mobileFilterForm);
      }
    });

    section.addEventListener('submit', function (event) {
      var form = event.target.closest('[data-up-collection-filter-form], [data-up-collection-sort-form]');
      if (!form) return;
      event.preventDefault();
      submitFormViaFetch(form).then(function () {
        if (form.id.indexOf('-mobile') !== -1) closeDrawer();
      });
    });

    section.addEventListener('click', function (event) {
      if (event.target.closest('[data-up-collection-float-btn]')) {
        openDrawer();
        return;
      }

      if (event.target.closest('[data-up-collection-drawer-close]')) {
        closeDrawer();
        return;
      }

      var tagLink = event.target.closest('[data-up-collection-tag-link]');
      if (tagLink) {
        event.preventDefault();
        fetchSwap(tagLink.href).then(function () {
          closeDrawer();
        });
        return;
      }

      var clearLink = event.target.closest('[data-up-collection-clear]');
      if (clearLink) {
        event.preventDefault();
        fetchSwap(clearLink.href).then(function () {
          closeDrawer();
        });
      }
    });
  }

  document.querySelectorAll('[data-up-collection]').forEach(initSection);

  window.addEventListener('popstate', function (event) {
    if (event.state && event.state.upCollection) window.location.reload();
  });
})();

/* --------------------------------------------------------------------------
   Sort popover. Measured on the reference: the listbox fades opacity 0 to 1
   over about 160ms with no transform. A native select cannot animate, so it
   stays in the DOM as the no-JS fallback and this replaces it visually.
   -------------------------------------------------------------------------- */
(function () {
  function enhance(wrap) {
    const select = wrap.querySelector('[data-up-collection-sort-select]');
    if (!select || wrap.dataset.upSortReady) return;
    wrap.dataset.upSortReady = '1';

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'up-collection__sort-button';
    button.setAttribute('aria-haspopup', 'listbox');
    button.setAttribute('aria-expanded', 'false');

    const value = document.createElement('span');
    value.className = 'up-collection__sort-value';
    button.appendChild(value);
    button.insertAdjacentHTML('beforeend',
      '<svg class="up-collection__sort-chevron" viewBox="0 0 10 7" aria-hidden="true" focusable="false">' +
      '<path d="M1 1.5 5 5.5 9 1.5" fill="none" stroke="currentColor" stroke-width="1.5" ' +
      'stroke-linecap="round" stroke-linejoin="round"/></svg>');

    const list = document.createElement('ul');
    list.className = 'up-collection__sort-list';
    list.setAttribute('role', 'listbox');
    list.hidden = true;

    [...select.options].forEach((opt, i) => {
      const li = document.createElement('li');
      li.className = 'up-collection__sort-option';
      li.setAttribute('role', 'option');
      li.tabIndex = -1;
      li.textContent = opt.textContent.trim();
      li.dataset.value = opt.value;
      li.addEventListener('click', () => {
        select.selectedIndex = i;
        select.dispatchEvent(new Event('change', { bubbles: true }));
        sync();
        close();
        button.focus();
      });
      list.appendChild(li);
    });

    function sync() {
      const opt = select.options[select.selectedIndex];
      value.textContent = opt ? opt.textContent.trim() : '';
      [...list.children].forEach((li, i) => {
        const on = i === select.selectedIndex;
        li.setAttribute('aria-selected', on ? 'true' : 'false');
        li.classList.toggle('is-selected', on);
      });
    }

    let open = false;
    function show() {
      if (open) return;
      open = true;
      list.hidden = false;
      requestAnimationFrame(() => list.classList.add('is-open'));
      button.setAttribute('aria-expanded', 'true');
    }
    function close() {
      if (!open) return;
      open = false;
      list.classList.remove('is-open');
      button.setAttribute('aria-expanded', 'false');
      const done = () => { if (!open) list.hidden = true; list.removeEventListener('transitionend', done); };
      list.addEventListener('transitionend', done);
      setTimeout(done, 260);
    }

    button.addEventListener('click', () => (open ? close() : show()));
    document.addEventListener('click', e => { if (!wrap.contains(e.target)) close(); });
    document.addEventListener('keydown', e => { if (e.key === 'Escape' && open) { close(); button.focus(); } });
    select.addEventListener('change', sync);

    wrap.appendChild(button);
    wrap.appendChild(list);
    wrap.classList.add('up-collection__sort-inner--enhanced');
    sync();
  }

  const init = () => document.querySelectorAll('[data-up-sort]').forEach(enhance);
  document.addEventListener('DOMContentLoaded', init);
  document.addEventListener('shopify:section:load', init);
  init();
})();

/* --------------------------------------------------------------------------
   Smooth accordion height. A <details> reveals its content in one jump, so
   the fade alone still reads as a snap. Wrap the content in a grid row that
   animates 0fr to 1fr, and hold the element open while it closes so the
   closing direction animates too. Falls back to the native jump without JS.
   -------------------------------------------------------------------------- */
(function () {
  const DURATION = 260;

  function enhance(details) {
    if (details.dataset.upAccordionReady) return;
    const content = details.querySelector('.up-collection__accordion-content');
    const summary = details.querySelector('summary');
    if (!content || !summary) return;
    details.dataset.upAccordionReady = '1';

    const panel = document.createElement('div');
    panel.className = 'up-collection__accordion-panel';
    content.parentNode.insertBefore(panel, content);
    panel.appendChild(content);
    if (details.open) panel.classList.add('is-open');

    let animating = false;

    summary.addEventListener('click', event => {
      event.preventDefault();
      if (animating) return;
      animating = true;

      if (!details.open) {
        details.open = true;
        requestAnimationFrame(() => requestAnimationFrame(() => panel.classList.add('is-open')));
        setTimeout(() => { animating = false; }, DURATION);
      } else {
        panel.classList.remove('is-open');
        setTimeout(() => {
          details.open = false;
          animating = false;
        }, DURATION);
      }
    });
  }

  const init = () => document.querySelectorAll('.up-collection__accordion').forEach(enhance);
  document.addEventListener('DOMContentLoaded', init);
  document.addEventListener('shopify:section:load', init);
  init();
})();
